import dayjs from 'dayjs';

import './dayjsLocaleSetup';
import { DEFAULT_LOCALE } from './constants';

const DEFAULT_DAYJS_LOCALE = DEFAULT_LOCALE.toLowerCase();

/**
 * Normalises a browser/BCP-47 tag (e.g. `en-US`, `de-DE`) to a dayjs locale we've bundled.
 *
 * dayjs registers most languages without a region (`de`, not `de-DE`), and its built-in `en`
 * is *US* English. So an exact match wins first; otherwise we fall back to the language. The
 * one trap is English: blindly using the `en` language subtag would turn every non-US English
 * region (en-AU, en-CA, en-NZ, …) into US month-first dates, so unbundled English regions fall
 * back to the day-first default (en-GB) and only genuine US English uses `en`.
 */
const toDayjsLocale = (tag: string): string => {
  const lower = tag.toLowerCase();

  if (dayjs.Ls[lower]) {
    return lower;
  }

  const [language, region] = lower.split('-');

  if (language === 'en') {
    return region === 'us' ? 'en' : DEFAULT_DAYJS_LOCALE;
  }

  return dayjs.Ls[language] ? language : DEFAULT_DAYJS_LOCALE;
};

/** The user's preferred locale from the browser; falls back to the default off-browser (SSR, tests). */
const browserLocale = (): string =>
  (typeof navigator !== 'undefined' && (navigator.languages?.[0] ?? navigator.language)) || DEFAULT_LOCALE;

/**
 * Sets dayjs's global locale (used by all `format` calls) and returns the resolved dayjs
 * locale name so callers (e.g. MUI's LocalizationProvider) can use the same one. Defaults to
 * the browser's locale — dayjs itself never reads `navigator`; its default is `en` until told
 * otherwise.
 */
export const setDateLocale = (tag: string = browserLocale()): string => {
  const locale = toDayjsLocale(tag);
  dayjs.locale(locale);

  return locale;
};
